/**
 * XamunPlanner — full multi-stage port of `Design/planner.jsx` (Plan 7
 * follow-up). State machine dispatches between Chooser, Quick Launch,
 * Discovery, Template, Hybrid, Editor (Timeline / Graph / Kanban), and
 * Review. In-flight state lives in `usePlannerStore` so navigating away
 * mid-flow doesn't lose progress.
 *
 * The previous V1 implementation only shipped the Chooser + a hand-off
 * to DiscoveryChat; that is preserved as the chooser's discovery card,
 * but the new Discovery stage is embedded directly so the materializing
 * playbook canvas (right rail) renders alongside the interview.
 */

import { useParams, useSearchParams } from "react-router-dom";
import { usePlannerSession } from "../../state/usePlannerStore.js";
import {
  ChooserStage,
  DiscoveryStage,
  EditorStage,
  HybridStage,
  QuickStage,
  ReviewStage,
  TemplateStage,
} from "./planner/stages.js";

export function Planner() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const [searchParams] = useSearchParams();
  // Accept both `roadmapEntryId` (new spec) and `entry` (legacy V1 query
  // param) so existing inbox links keep working.
  const roadmapEntryId =
    searchParams.get("roadmapEntryId") ?? searchParams.get("entry") ?? "";
  const initiativeTitle =
    searchParams.get("title") ?? "Plan a new play";
  const playId = searchParams.get("playId") ?? roadmapEntryId;

  const { session } = usePlannerSession(roadmapEntryId);
  const stageProps = {
    workspaceId,
    entryId: roadmapEntryId,
    initiativeTitle,
    playId,
  };

  switch (session.stage) {
    case "chooser":
      return <ChooserStage {...stageProps} />;
    case "quick":
      return <QuickStage {...stageProps} />;
    case "discovery":
      return <DiscoveryStage {...stageProps} />;
    case "template":
      return <TemplateStage {...stageProps} />;
    case "hybrid":
      return <HybridStage {...stageProps} />;
    case "editor":
      return <EditorStage {...stageProps} />;
    case "review":
      return <ReviewStage {...stageProps} />;
    default:
      return <ChooserStage {...stageProps} />;
  }
}
